/**
 * Tap-to-pay step — walks staff through taking the exact amount in the Stripe
 * Dashboard app. The invoice already exists, so the webhook matches the tap to
 * it by amount; settlement shows up when the parent's poll drops the orders
 * from the cart, so there is no success state here.
 */
import { ExternalLink, Loader2 } from 'lucide-react'
import { useEffect } from 'react'

import type { ClinicId, OrderWithOwnerStripeUrl } from '@snootsvet/shared'
import { formatClinicPrice } from '@snootsvet/shared'

import { Alert, AlertDescription, AlertTitle } from '@/frontend/components/ui/alert'

export interface TapToPayPanelProps {
  /** What the invoice still owes after any account credit — the amount to key in. */
  amountDue: number
  /** Sum of the order lines, shown alongside when credit reduced the amount due. */
  total: number
  clinicId: ClinicId
  owner: OrderWithOwnerStripeUrl['patient']['owner']
  /** Email on the invoice's Stripe customer; null when Stripe holds none. */
  customerEmail: string | null
  /** Reports that an invoice is open and waiting for the tap to land. */
  onAwaitingPaymentChange: (awaiting: boolean) => void
}

export function TapToPayPanel({
  amountDue,
  total,
  clinicId,
  owner,
  customerEmail,
  onAwaitingPaymentChange,
}: TapToPayPanelProps) {
  useEffect(() => {
    onAwaitingPaymentChange(true)
    return () => onAwaitingPaymentChange(false)
  }, [onAwaitingPaymentChange])

  const emailsDiffer =
    !!customerEmail?.trim() && customerEmail.toLowerCase() !== owner.email.toLowerCase()

  const amount = formatClinicPrice(amountDue, clinicId)
  const creditApplied = total - amountDue

  return (
    <div className="flex flex-col gap-4">
      {customerEmail?.trim() ? (
        <>
          <div className="flex flex-col items-center gap-1 rounded-lg border border-border bg-muted/40 py-4">
            <span className="text-sm text-muted-foreground">Charge exactly</span>
            <span className="text-4xl font-semibold tabular-nums">{amount}</span>
            {creditApplied > 0 && (
              <span className="text-sm text-muted-foreground">
                {formatClinicPrice(creditApplied, clinicId)} of {formatClinicPrice(total, clinicId)}{' '}
                covered by account credit
              </span>
            )}
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Waiting for payment…
            </span>
          </div>

          <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
            <li>Open the Stripe Dashboard app and tap the + button.</li>
            <li>Choose “Charge a card or send an invoice” and enter {amount}.</li>
            <li>
              Set the customer to{' '}
              <span className="font-medium text-foreground">{customerEmail}</span>.
              {emailsDiffer && (
                <span className="block text-sm">
                  This is the email on their Stripe account, not the one on file here ({owner.email}
                  ).
                </span>
              )}
            </li>
            <li>Select Tap to Pay and hold the card to the phone.</li>
          </ol>

          <p className="text-sm text-muted-foreground">A different amount won't match.</p>
        </>
      ) : (
        <Alert variant="destructive">
          <AlertTitle>Tap to Pay unavailable</AlertTitle>
          <AlertDescription>
            This Stripe account has no email. Ask the engineering team to fix the account. In the
            meantime, select a saved card above to take payment.
          </AlertDescription>
        </Alert>
      )}

      {owner.stripeDashboardUrl && (
        <a
          href={owner.stripeDashboardUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          Open member in Stripe
          <ExternalLink className="size-3" />
        </a>
      )}
    </div>
  )
}
